import type { FormEvent } from 'react'
import { useMemo, useCallback, memo, useState } from 'react'

import {
  faArrowDown,
  faArrowUp,
  faClone,
  faFillDrip,
  faRemove,
  faSquareCheck,
  faTrash,
  faUpDown,
  faObjectUngroup,
  faObjectGroup,
} from '@fortawesome/free-solid-svg-icons'

import { useSetAtom } from 'jotai'

import { CueType } from '~shared/rundown/cue'

import {
  getRealCueIndex,
  useGroupRundownCues,
  useDuplicateRundownCues,
  useUngroupRundownCues,
} from '@/entities/rundown'

import {
  cueUiActions,
  useSelectedCueIds,
} from '@/features/rundown/cue/cue-ui.store'
import {
  getCueOrder,
  useCueOrder,
  useCuesDerived,
} from '@/features/rundown/cue/cue.derived'
import { CueColorPicker } from '@/features/rundown/cue/CueColorPicker'
import { requireRundown } from '@/features/rundown/data/rundown.store'
import { useRundownSettings } from '@/features/rundown/rundown.derived'

import {
  deleteRundownCues,
  updateRundownCues,
  moveRundownCuesAfter,
} from '../../firestore'
import { captureException } from '../../sentry'
import { toastAtom } from '../../store/toast.store'
import { Button } from '../button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../interactives/Popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../interactives/Tooltip'
import ModalWrapper from '../modal/ModalWrapper'

interface BatchEditToolbarProps {
  rundownId: string
  setRundownColoursModalOpen?: (open: boolean) => void
}

function BatchEditToolbar({
  rundownId,
  setRundownColoursModalOpen,
}: BatchEditToolbarProps) {
  const selectedCueIds = useSelectedCueIds()
  const { sortedCues, indexedCuesMap } = useCuesDerived()
  // Root-level order as shown, for select all and the group/move anchors.
  const cueOrder = useCueOrder()
  const rundownSettings = useRundownSettings()
  const [moveInput, setMoveInput] = useState('')
  const [loadingMove, setLoadingMove] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [loadingBackground, setLoadingBackground] = useState(false)
  const [deletingGroups, setDeletingGroups] = useState(false)
  const addToast = useSetAtom(toastAtom)

  const { mutate: groupRundownCues, isPending: isGroupingCues } =
    useGroupRundownCues()
  const { mutate: duplicateRundownCues, isPending: isDuplicatingCues } =
    useDuplicateRundownCues()
  const { mutate: ungroupCues, isPending: isUngroupingCues } =
    useUngroupRundownCues()

  const groupsHeadersSelected = useMemo(() => {
    return (
      selectedCueIds
        .map((cueId) => indexedCuesMap[cueId]?.type)
        .find((type) => type === CueType.GROUP) !== undefined
    )
  }, [indexedCuesMap, selectedCueIds])
  const groupChildrenSelected = useMemo(() => {
    // Children carry `parentId` in the indexed list; see `buildIndexedCues`.
    return selectedCueIds.some(
      (cueId) => indexedCuesMap[cueId]?.parentId !== undefined,
    )
  }, [indexedCuesMap, selectedCueIds])

  const isUngroupDisabled = useMemo(() => {
    return !groupChildrenSelected && !groupsHeadersSelected
  }, [groupChildrenSelected, groupsHeadersSelected])

  const clearSelection = useCallback(() => {
    cueUiActions.clearSelected()
  }, [])

  function selectAllCues() {
    cueUiActions.setSelected(cueOrder.map((c) => c.id))
  }

  // this will create a group with the children in the order of selection
  async function groupCues() {
    const firstSelected = cueOrder.find((cue) =>
      selectedCueIds.includes(cue.id),
    )?.id

    if (!firstSelected) return

    // Get selected cue IDs in the same order as they appear in cueOrder
    const cuesToGroupIds = cueOrder
      .filter((cue) => selectedCueIds.includes(cue.id))
      .map((cue) => cue.id)

    groupRundownCues(
      {
        rundownId,
        beforeCueId: firstSelected,
        cuesToGroupIds,
      },
      {
        onSuccess() {
          clearSelection()
        },
      },
    )
  }

  const handleUngroupCues = useCallback(() => {
    ungroupCues(
      {
        rundownId,
        payload: {
          cueIds: [...selectedCueIds],
        },
      },
      {
        onSuccess() {
          clearSelection()
        },
      },
    )
  }, [clearSelection, rundownId, selectedCueIds, ungroupCues])

  function duplicateCues() {
    duplicateRundownCues(
      {
        rundownId,
        payload: {
          cueIds: [...selectedCueIds],
        },
      },
      {
        onSuccess() {
          clearSelection()
        },
      },
    )
  }

  function onChangeMoveInput(event: React.ChangeEvent<HTMLInputElement>) {
    setMoveInput(event.target.value)
  }

  async function moveToBeginning() {
    setLoadingMove(true)
    try {
      await moveRundownCuesAfter(rundownId, selectedCueIds, null)
      clearSelection()
      setMoveInput('')
    } catch (error) {
      captureException(error)
    } finally {
      setLoadingMove(false)
    }
  }

  async function moveToEnd() {
    setLoadingMove(true)
    try {
      // Anchor on the last root-level cue that is not itself moving: the
      // server extracts the selection before looking the target up, so a
      // selected anchor would no longer exist. If everything is selected
      // there is nothing to anchor to, and start and end are the same place.
      const anchor = cueOrder.findLast(
        (cue) => !selectedCueIds.includes(cue.id),
      )
      await moveRundownCuesAfter(rundownId, selectedCueIds, anchor?.id ?? null)
      clearSelection()
      setMoveInput('')
    } catch (error) {
      captureException(error)
    } finally {
      setLoadingMove(false)
    }
  }

  async function moveCues(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault()
    setLoadingMove(true)
    // Read once at call time, like the other handlers, not from the render.
    const rundown = requireRundown()
    try {
      if (moveInput === '0') return moveToBeginning()

      // The order the user sees, so a pending drop is not undone by the move.
      const realIndex = getRealCueIndex(moveInput, sortedCues, getCueOrder(), {
        startFrom: rundown.settings.cueIndexStartFrom,
        padding: rundown.settings.cueIndexPadding,
      })
      if (realIndex === null) return
      const cueId = sortedCues?.[realIndex]?.id
      await moveRundownCuesAfter(rundownId, selectedCueIds, cueId)
      clearSelection()
      setMoveInput('')
    } catch (error) {
      captureException(error)
    } finally {
      setLoadingMove(false)
    }
  }

  async function updateBackground(backgroundColor: string) {
    setLoadingBackground(true)
    try {
      await updateRundownCues(rundownId, selectedCueIds, { backgroundColor })
      clearSelection()
    } finally {
      setLoadingBackground(false)
    }
  }

  async function deleteCues({ confirm = false }: { confirm?: boolean } = {}) {
    if (!confirm && groupsHeadersSelected) return setDeletingGroups(true)
    setLoadingDelete(true)
    try {
      await deleteRundownCues(rundownId, selectedCueIds).then(() =>
        addToast({ title: `Cues moved to trash`, type: 'success' }),
      )
      clearSelection()
    } catch (error) {
      captureException(error)
    } finally {
      setLoadingDelete(false)
    }
  }

  return (
    selectedCueIds.length > 0 && (
      <div className="z-50 fixed mx-2 p-1.5 bg-gray-900 ring-2 ring-black/70 backdrop-blur-sm rounded-sm w-[822px] flex text-sm font-semibold justify-between flex-wrap">
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            colour="dark"
            text="Select all"
            onClick={selectAllCues}
            icon={faSquareCheck}
          />
          <Button
            type="button"
            size="sm"
            colour="dark"
            text="Duplicate"
            icon={faClone}
            onClick={duplicateCues}
            loading={isDuplicatingCues}
          />
          <Button
            type="button"
            size="sm"
            colour="dark"
            text="Group"
            onClick={groupCues}
            disabled={groupChildrenSelected || groupsHeadersSelected}
            loading={isGroupingCues}
            showTooltipWhenDisabled
            toolTip={
              groupChildrenSelected || groupsHeadersSelected
                ? 'Cannot group already grouped cues'
                : ''
            }
            icon={faObjectGroup}
          />
          <Button
            type="button"
            size="sm"
            colour="dark"
            text="Ungroup"
            onClick={handleUngroupCues}
            disabled={isUngroupDisabled}
            loading={isUngroupingCues}
            showTooltipWhenDisabled
            toolTip={
              isUngroupDisabled
                ? 'Select an entire group or any cue within a group to ungroup'
                : ''
            }
            icon={faObjectUngroup}
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="sm"
                colour="dark"
                text="Move"
                loading={loadingMove}
                icon={faUpDown}
              />
            </PopoverTrigger>
            <PopoverContent
              hideButtons
              onCancel={setMoveInput.bind(null, '')}
            >
              <div className="flex flex-col gap-2">
                <div>
                  <p className="leading-3 text-sm text-gray-300 mb-2">
                    Move to:
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      text="Start"
                      size="sm"
                      className="justify-start! w-full"
                      icon={faArrowUp}
                      colour="dark"
                      disabled={loadingMove}
                      onClick={moveToBeginning}
                    />
                    <Button
                      text="End"
                      size="sm"
                      className="justify-start! w-full"
                      icon={faArrowDown}
                      colour="dark"
                      disabled={loadingMove}
                      onClick={moveToEnd}
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="target-cue"
                    className="block leading-3 text-sm text-gray-300 mb-2"
                  >
                    Insert after:
                  </label>
                  <form
                    className="grid grid-cols-2 gap-2"
                    onSubmit={moveCues}
                  >
                    <Tooltip placement="bottom">
                      <TooltipTrigger>
                        <span className="inline-flex items-center">
                          {rundownSettings.cueIndexPrefix && (
                            <span className="text-gray-400 text-sm mr-0.5">
                              {rundownSettings.cueIndexPrefix}
                            </span>
                          )}
                          <input
                            id="target-cue"
                            type="number"
                            title=""
                            min={0}
                            step={0.01}
                            value={moveInput}
                            onChange={onChangeMoveInput}
                            className="w-20 py-0.5 bg-gray-800 hover:brightness-125 rounded-sm text-center focus:outline-hidden focus:ring-3 appearance-none [-moz-appearance:textfield]"
                            placeholder="2"
                            required
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="text-center">
                        Enter the number of a cue or sub-cue. Example: 2.1
                      </TooltipContent>
                    </Tooltip>
                    <Button
                      text="Move"
                      size="sm"
                      className="w-full"
                      colour="dark"
                      disabled={loadingMove}
                      type="submit"
                    />
                  </form>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="sm"
                colour="dark"
                text="Background"
                loading={loadingBackground}
                icon={faFillDrip}
              />
            </PopoverTrigger>
            <PopoverContent hideButtons>
              <CueColorPicker
                onClick={updateBackground}
                disabled={loadingBackground}
              />
              <Button
                text="Edit rundown colors"
                size="sm"
                className="mx-auto mt-2"
                colour="dark"
                disabled={loadingBackground}
                onClick={setRundownColoursModalOpen?.bind(null, true)}
              />
            </PopoverContent>
          </Popover>
          <Button
            type="button"
            size="sm"
            colour="dark"
            text="Delete"
            onClick={deleteCues}
            loading={loadingDelete}
            icon={faTrash}
          />
        </div>
        <div>
          <Button
            type="button"
            size="sm"
            colour="dark"
            text="Clear selection"
            onClick={clearSelection}
            icon={faRemove}
          />
        </div>
        {deletingGroups && (
          <ModalWrapper
            open={[deletingGroups, setDeletingGroups]}
            title="Delete Group"
            buttons={[
              {
                text: 'Cancel',
                onClick: () => setDeletingGroups(false),
              },
              {
                text: 'Delete Group',
                icon: faTrash,
                onClick: async () => {
                  setDeletingGroups(false)
                  void deleteCues({ confirm: true })
                },
              },
            ]}
          >
            <p>
              Deleting this group will permanently remove all cues within it.
            </p>
          </ModalWrapper>
        )}
      </div>
    )
  )
}

export default memo(BatchEditToolbar)
